// frontend/src/features/production/shortageHold/api.ts
// ADR-105 + AM-016.3 — ALL orval-generated hook imports for this feature
// are isolated in this ONE file. Every page/component in this feature
// imports ONLY from here, never directly from "@/api/generated/v1/v1".
//
// ⚠️ MUST VERIFY BEFORE FIRST RUN — READ THIS ⚠️
// The exact hook names below were never generated by a live backend in
// this session (I cannot run `npm run api:generate` without a running
// Django server + DB). They follow the exact naming pattern already
// observed in the codebase (see
// frontend/src/features/production/serialization/api/serializationApi.ts:
// useV1Production<Resource><Action>, derived from drf-spectacular's
// operationId = <domain>_<url-path>_<method>). The underlying endpoints
// themselves ARE confirmed to exist (verified against
// apps/production/api/urls.py and apps/inventory/api/urls.py):
//   - work-orders, bom-operation-allocations  (production router)
//   - locations                                (inventory router)
//   - shortage-holds (+ qc-approve/resume actions) (shortage_hold router)
//
// Steps to fix if names don't match after `npm run api:generate`:
// 1. Run the two commands below from the repo root.
// 2. Open frontend/src/api/generated/v1/v1.ts and search for "WorkOrder",
//    "BomOperationAllocation", "Location", and "ShortageHold".
// 3. Update ONLY the import block below with the real names — no other
//    file in this feature needs to change.
//
//   cd backend && venv\Scripts\python.exe manage.py spectacular --file ../frontend/schema.yml --validate
//   cd frontend && npm run api:generate

import {
  // WorkOrderShortageHold
  useV1ProductionShortageHoldsList,
  useV1ProductionShortageHoldsCreate,
  useV1ProductionShortageHoldsQcApproveCreate,
  useV1ProductionShortageHoldsResumeCreate,
  // WorkOrder (already exists — ADR-102)
  useV1ProductionWorkOrdersList,
  useV1ProductionWorkOrdersRetrieve,
  // BomOperationAllocation (already exists — ADR-101/AM-011)
  useV1ProductionBomOperationAllocationsList,
  // Location (already exists — ADR-054, extended by ADR-105 patch)
  useV1InventoryLocationsList,
} from "@/api/generated/v1/v1";

import type {
  BomOperationAllocationSummary,
  CreateShortageHoldPayload,
  LocationSummary,
  WorkOrderShortageHold,
  WorkOrderSummary,
} from "./types";

function unwrapList<T>(data: any): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : (data.results ?? []);
}

// -- Shortage Holds ----------------------------------------------------

export function useShortageHoldsList(params?: { work_order?: string; status?: string }) {
  const query = useV1ProductionShortageHoldsList(params, { query: {} });
  return { ...query, data: unwrapList<WorkOrderShortageHold>(query.data) };
}

export function useCreateShortageHold() {
  const mutation = useV1ProductionShortageHoldsCreate();
  return {
    ...mutation,
    mutateAsync: (payload: CreateShortageHoldPayload) =>
      mutation.mutateAsync({ data: payload }),
  };
}

export function useQcApproveShortageHold() {
  const mutation = useV1ProductionShortageHoldsQcApproveCreate();
  return {
    ...mutation,
    mutateAsync: (vars: { id: string; approved_by_id: string }) =>
      mutation.mutateAsync({ id: vars.id, data: { approved_by_id: vars.approved_by_id } }),
  };
}

export function useResumeShortageHold() {
  const mutation = useV1ProductionShortageHoldsResumeCreate();
  return {
    ...mutation,
    mutateAsync: (vars: { id: string; exit_transaction?: string }) =>
      mutation.mutateAsync({
        id: vars.id,
        data: vars.exit_transaction ? { exit_transaction: vars.exit_transaction } : {},
      }),
  };
}

// -- Entity pickers (AM-016.1) -------------------------------------------

export function useWorkOrdersList() {
  const query = useV1ProductionWorkOrdersList(undefined, { query: {} });
  return { ...query, data: unwrapList<WorkOrderSummary>(query.data) };
}

export function useWorkOrderRetrieve(id: string | undefined) {
  return useV1ProductionWorkOrdersRetrieve(id as string, { query: { enabled: !!id } });
}

export function useBomOperationAllocationsList() {
  const query = useV1ProductionBomOperationAllocationsList(undefined, { query: {} });
  return { ...query, data: unwrapList<BomOperationAllocationSummary>(query.data) };
}

export function useLocationsList() {
  const query = useV1InventoryLocationsList(undefined, { query: {} });
  return { ...query, data: unwrapList<LocationSummary>(query.data) };
}

export type { CreateShortageHoldPayload };
